<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-report-stock-reorder">
        <i class="far fa-ballot"></i> Stock Reorder Report
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-report-stock-reorder">
        <div class="row">
          <div class="col-lg-12">
            <DataTable ref="stockReorderTable" :columns="columnList" defaultColumnOrderDir="DESC" :defaultColumnOrderIndex="0" :url="reorderReportDataURL" :displayExportTools="true" exportTitle="Stock Reorder Report" :displayTools="true">
              <template v-slot:title>
                <ContentTitle>Stock Reorder Report</ContentTitle>
                <ContentSubTitle>Showing incoming stock from {{appliedStockReorderFilter.incomingStockStartDate | formatDateCustom('MMM YYYY')}} to {{appliedStockReorderFilter.incomingStockEndDate | formatDateCustom('MMM YYYY')}}</ContentSubTitle>
                <ContentSubTitle>Showing stock usage from {{appliedStockReorderFilter.stockUsageStartDate | formatDateCustom('MMM YYYY')}} to {{appliedStockReorderFilter.stockUsageEndDate | formatDateCustom('MMM YYYY')}}</ContentSubTitle>
              </template>
              <template v-slot:tableHeader>
                <DataTableTopHeaderRow>
                  <DataTableHeaderCell colspan="6"></DataTableHeaderCell>
                  <DataTableHeaderCell :colspan="incomingStockMonthList.length">Incoming Stock</DataTableHeaderCell>
                </DataTableTopHeaderRow>
              </template>
              <template v-slot:tools>
                <ReportFilterPopover
                  class="float-right"
                  headerTitle="Stock Reorder Filters"
                  :showApplyButton="true"
                  :showResetButton="true"
                  @apply="applyStockReorderFilter"
                  @reset="resetStockReorderFilter"
                >
                  <template v-slot:popover>
                    <ReportFilterGroup label="Incoming Stock Date Range" subTitle="Date range when purchase order is expected to arrive">
                      <ReportDateRangeSelect 
                        :startDate="editingStockReoderFilter.incomingStockStartDate"
                        :endDate="editingStockReoderFilter.incomingStockEndDate"
                        @updateStartDate="editingStockReoderFilter.incomingStockStartDate = $event"
                        @updateEndDate="editingStockReoderFilter.incomingStockEndDate = $event"
                      />
                    </ReportFilterGroup>
                    <ReportFilterGroup label="Purchase Order Status Filter">
                      <ReportMultiSelect :options="PurchaseOrderStatusTypeLookup" v-model="editingStockReoderFilter.poStatusList" />
                    </ReportFilterGroup>
                    <ReportFilterGroup label="Stock Usage Date Range" subTitle="Date range for stock usage data">
                      <ReportDateRangeSelect 
                        :startDate="editingStockReoderFilter.stockUsageStartDate"
                        :endDate="editingStockReoderFilter.stockUsageEndDate"
                        @updateStartDate="editingStockReoderFilter.stockUsageStartDate = $event"
                        @updateEndDate="editingStockReoderFilter.stockUsageEndDate = $event"
                      />
                    </ReportFilterGroup>
                  </template>
                  <template v-slot:filterDisplay>
                    <div>
                      <Tooltip label="Incoming stock date range">
                        <i class="far fa-arrow-alt-to-bottom mr-2"></i>{{appliedStockReorderFilter.incomingStockStartDate | formatDateCustom('MMM YYYY')}} - {{appliedStockReorderFilter.incomingStockEndDate | formatDateCustom('MMM YYYY')}}
                      </Tooltip>
                    </div>
                    <div>
                      <Tooltip label="Stock usage date range">
                        <i class="far fa-arrow-alt-from-bottom mr-2"></i>{{appliedStockReorderFilter.stockUsageStartDate | formatDateCustom('MMM YYYY')}} - {{appliedStockReorderFilter.stockUsageEndDate | formatDateCustom('MMM YYYY')}}
                      </Tooltip>
                    </div>
                  </template>
                </ReportFilterPopover>
              </template>
              <template v-slot:row="row">
                <tr :key="row.data.id">
                  <td class="table-data-nav-link">
                    <LinkStock :id="row.data.stockId" :name="row.data.stockName" />
                  </td>
                  <td>
                    {{row.data.outputSize}}
                  </td>
                  <td>
                    {{row.data.onHandTotalSheets}}
                  </td>
                  <td>
                    {{row.data.stockUsageTotalSheets}}
                  </td>
                  <td>
                    {{row.data.stockManualTotalSheets}}
                  </td>
                  <td>
                    {{row.data.averageStockUsage}}
                  </td>
                  <template v-for="(monthInfo, index) of incomingStockMonthList">
                    <td :key="index" class="table-data-alternate-column">
                      {{row.data[monthInfo.data]}}
                    </td>
                  </template>
                  <td>
                    {{row.data.futureEstStockUsage}}
                  </td>
                  <td :class="{'text-center': true, 'financial-loss': row.data.surplusSheets < 0, 'financial-profit': row.data.surplusSheets > 0}">
                    {{row.data.surplusSheets}}
                  </td>
                  <td>
                    {{row.data.blockSize}}
                  </td>
                  <td :class="{'text-center': true, 'financial-loss': row.data.surplusBlocks < 0, 'financial-profit': row.data.surplusBlocks > 0}">
                    {{row.data.surplusBlocks}}
                  </td>
                </tr>
              </template>
            </DataTable>
          </div>
        </div>
      </TabPage>

    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import StaticDataTable from '@/components/table/staticDataTable'
import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportMultiSelect from '@/components/reporting/reportMultiSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import { PurchaseOrderStatusTypes, PurchaseOrderStatusTypeLookup } from '@/store/types'

export default {
  name: 'StockReorder',
  components: {
    StaticDataTable,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportMultiSelect,
    ReportFilterGroup
  },
  created() {
    this.PurchaseOrderStatusTypeLookup = PurchaseOrderStatusTypeLookup
  },
  data() {
    return {
      defaultStockReorderFilter: {
        incomingStockStartDate: moment().local().startOf('month').startOf('day').toDate().toISOString(),
        incomingStockEndDate: moment().local().startOf('month').add(5, 'months').endOf('month').endOf('day').toDate().toISOString(),
        stockUsageStartDate: moment().local().startOf('month').subtract(5, 'months').startOf('month').startOf('day').toDate().toISOString(),
        stockUsageEndDate: moment().local().endOf('month').endOf('day').toDate().toISOString(),
        poStatusList: [
          PurchaseOrderStatusTypes.pendingConfirmation,
          PurchaseOrderStatusTypes.pendingShipping,
          PurchaseOrderStatusTypes.pendingReceival,
          PurchaseOrderStatusTypes.receiving
        ]
      },
      editingStockReoderFilter: {},
      appliedStockReorderFilter: {}
    }
  },
  computed: {
    reorderReportDataURL() {
      if(_.isEmpty(this.appliedStockReorderFilter))
        return ''

      var params = {
        incomingStockStartDate: this.appliedStockReorderFilter.incomingStockStartDate,
        incomingStockEndDate: this.appliedStockReorderFilter.incomingStockEndDate,
        stockUsageStartDate: this.appliedStockReorderFilter.stockUsageStartDate,
        stockUsageEndDate: this.appliedStockReorderFilter.stockUsageEndDate,
        poStatusList: this.appliedStockReorderFilter.poStatusList
      }

      // Select an empty status if none set to filter out all POs if no status types selected
      if(params.poStatusList.length == 0)
        params.poStatusList = ['']

      return 'intelligence/purchasing/stockreorder/report?' + $.param(params)
    },
    incomingStockMonthList() {
      if(_.isEmpty(this.appliedStockReorderFilter))
        return []

      let incomingStartDate = moment(this.appliedStockReorderFilter.incomingStockStartDate).local().startOf('month')
      let incomingEndDate = moment(this.appliedStockReorderFilter.incomingStockEndDate).local().endOf('month')
      let usageStartDate = moment(this.appliedStockReorderFilter.stockUsageStartDate).local().startOf('month')
      let usageEndDate = moment(this.appliedStockReorderFilter.stockUsageEndDate).local().endOf('month')

      let incomingStockMonthCount = Math.abs(parseInt(Math.round(moment.duration(incomingEndDate.diff(incomingStartDate)).asMonths())))
      let stockUsageMonthCount = Math.abs(parseInt(Math.round(moment.duration(moment(usageEndDate).diff(usageStartDate)).asMonths())))

      // Don't create too many columns
      // Server will also report errors with 12+ month ranges
      if(incomingStockMonthCount > 12 || stockUsageMonthCount > 12)
        return []

      var outputMonthList = []
      for(var monthIndex=0; monthIndex<incomingStockMonthCount; monthIndex++) {
        let monthField = `monthIncoming${monthIndex}`
        let monthName = moment(this.appliedStockReorderFilter.incomingStockStartDate).local().startOf('month').add(monthIndex, 'months').format('MMM')

        outputMonthList.push({ title: monthName, data: monthField })
      }

      return outputMonthList
    },
    columnList() {
      var list = [
        { title: "Stock", data: "stockName" },
        { title: "Size", data: "outputSize" },
        { title: "In Stock", data: "onHandTotalSheets" },
        { title: 'Stock Processed', data: 'stockUsageTotalSheets' },
        { title: 'Breakage & Handling', data: 'stockManualTotalSheets' },
        { title: 'Average Usage', data: 'averageStockUsage' },
      ]

      this.incomingStockMonthList.forEach(monthInfo => {
        list.push(monthInfo)
      })

      list = list.concat([
        { title: 'Usage Over Time', data: 'futureEstStockUsage' },
        { title: 'Surplus', data: 'surplusSheets' },
        { title: 'Sheets In Block', data: 'blockSize' },
        { title: 'Surplus Blocks', data: 'surplusBlocks' }
      ])

      return list
    }
  },
  watch: {
  },
  mounted() {
    this.editingStockReoderFilter = _.clone(this.defaultStockReorderFilter)
    this.appliedStockReorderFilter = _.clone(this.defaultStockReorderFilter)
    this.reloadReorderReport()
  },
  methods: {
    reload() {
      if(this.$refs.stockReorderTable)
        this.$refs.stockReorderTable.reload()
    },
    reloadReorderReport() {
      if(this.$refs.stockReorderTable)
        this.$refs.stockReorderTable.reload()
    },
    applyStockReorderFilter() {
      this.appliedStockReorderFilter = _.clone(this.editingStockReoderFilter)

      this.reloadReorderReport()
    },
    resetStockReorderFilter() {
      this.editingStockReoderFilter = _.clone(this.defaultStockReorderFilter)
      this.appliedStockReorderFilter = _.clone(this.defaultStockReorderFilter)

      this.reloadReorderReport()
    }
  }
  
}
</script>